import React, { useState, useEffect } from 'react';
import QuickbooksApi from '../quickbooksConnection/quickbooksApi';
import { getAccountIdFromSessionStorage } from '../lib/global_function';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import Spinner from '../components/ui/spinner';

const QuickbooksConnectPopup: React.FC = () => {
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [debugInfo, setDebugInfo] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState(false);

    // Extract parameters from URL and handle OAuth callback
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        // Check if this is a callback from QuickBooks OAuth (has code and state)
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const realmId = urlParams.get('realmId')

        if (code && state && realmId) {
            // This is the OAuth callback - process it
            handleOAuthCallback(code, state, realmId);
        }
    }, []);

    const handleOAuthCallback = async (code: string, state: string, realmId: string) => {
        setIsProcessing(true);

        try {
            // Use the API method to handle the callback
            const result = await QuickbooksApi.handleOAuthCallback(code, state, realmId);

            if (result.status === 'success') {
                // Notify parent window of successful authentication
                try {
                    if (window.opener && !window.opener.closed) {
                        window.opener.postMessage({
                            type: 'QUICKBOOKS_OAUTH_SUCCESS',
                            success: true
                        }, '*');

                        // Wait a moment then close
                        setTimeout(() => {
                            window.close();
                        }, 1500);
                    } else {
                        setError('Parent window not found. Please close this popup and try again.');
                    }
                } catch (error) {
                    //console.error('Error communicating with parent:', error);
                    setError('Error communicating with parent window.');
                }
            } else {
                setError(result.message || 'Authentication failed');
            }
        } catch (err) {
            //console.error('OAuth callback error:', err);
            const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
            setError(errorMessage);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSignOnClick = async () => {
        try {
            setIsConnecting(true);
            setError(null);

            // Get account ID from session storage (same as parent component)
            const accountId = getAccountIdFromSessionStorage(process.env.REACT_APP_SESSION_STORAGE_KEY as string);

            if (!accountId) {
                throw new Error('Account ID not found. Please log in again.');
            }

            // Use the current popup route as the callback path
            const front_href = 'quickbooks/connect';

            // Call your existing authenticate API - it will redirect the window
            await QuickbooksApi.authenticate(accountId, front_href);

        } catch (err) {
            //console.error('Authentication error:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to connect to QuickBooks';
            setError(errorMessage);
            setIsConnecting(false);
        }
    };

    // If we're processing the OAuth callback, show processing state
    if (isProcessing) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-8 bg-gray-50">
                <div className="w-full max-w-md p-8 bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="text-center mb-6">
                        <Spinner size="lg" fullScreen={false} className="mx-auto mb-4" />
                        <h2 className="text-2xl font-semibold text-primary mb-2">
                            Processing...
                        </h2>
                        <p className="text-gray-600">
                            Completing QuickBooks connection...
                        </p>
                    </div>

                    {/* Success indicator */}
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800 text-center">
                            Authentication successful! This window will close automatically.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center min-h-screen p-8 bg-gray-50">
            <div className="w-full max-w-md p-8 bg-white border border-gray-200 rounded-lg shadow-sm">
                {/* QuickBooks Logo and Header */}
                <div className="text-center mb-8">
                    <img
                        src="/qb_official_logo_48_48.png"
                        alt="QuickBooks"
                        className="h-[48px] w-[48px] mx-auto mb-6"
                    />
                    <h2 className="text-2xl font-semibold text-foreground mb-3">
                        Connect to QuickBooks Online
                    </h2>
                    <p className="text-gray-600 leading-relaxed">
                        Connect your QuickBooks Online account to send invoices directly from our platform.
                    </p>
                </div>

                {/* Debug Information (only in development) */}
                {process.env.NODE_ENV === 'development' && debugInfo && (
                    <div className="mb-6 p-4 bg-gray-100 border border-gray-300 rounded-lg">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Debug Info:</p>
                        <p className="text-xs text-gray-600 break-all">{window.location.href}</p>
                        <p className="text-xs text-gray-600">{debugInfo}</p>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <Alert variant="error" className="mb-6">
                        {error}
                    </Alert>
                )}

                {/* Sign On Button */}
                <div className="mb-6 text-center">
                    <button
                        onClick={handleSignOnClick}
                        disabled={isConnecting}
                    >
                        <img
                            src="/Sign_in_blue_qb_official.png"
                            alt="Sing in to QuickBooks"
                        />
                    </button>
                </div>

                {/* Footer Text */}
                <div className="text-center">
                    <p className="text-xs text-gray-500">
                        You will be redirected to QuickBooks for secure authentication.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default QuickbooksConnectPopup;